import { FC, PropsWithChildren, ReactNode } from 'react';
import { classNames } from 'primereact/utils';
import { LoadingSpinner, Layout, TopPageNavigation } from '@repo/atomic-components';
import { Header } from './Header.tsx';

type CaseTemplateProps = PropsWithChildren<{
  isDataLoading: boolean;
  actionsPanel: ReactNode;
  withNavigation?: boolean;
  /** Full-width notice rendered above the header, outside the grid, so it
   * pushes the page down instead of eating into the content row. */
  banner?: ReactNode;
  /** Content for an optional 320px left rail rendered below the header.
   * When omitted the page renders single-column (case-list behaviour);
   * when provided the rail appears and the main content shifts right. */
  leftSidebar?: ReactNode;
  onBackTopPageNavigationClick?: () => void;
}>;

export const CaseTemplate: FC<CaseTemplateProps> = ({
  children,
  isDataLoading,
  actionsPanel,
  withNavigation,
  banner,
  leftSidebar,
  onBackTopPageNavigationClick = () => {},
}) => {
  const hasLeftSidebar = Boolean(leftSidebar);
  return (
    <Layout>
      {/* Column wrapper so an optional `banner` can take its natural height
          off the top and the grid below claims whatever is left
          (`min-h-0 flex-1` in place of the grid's old `h-full`). With no
          banner the grid is the only child and fills the height as before. */}
      <div className="flex h-full w-full flex-col">
        {banner}
        {/* Grid columns: optionally a 320px left rail (opt-in via
            `withLeftSidebar`) plus the main content. The content column
            uses `minmax(0,1fr)` (not plain `1fr`, which is
            `minmax(auto,1fr)`) so its min-size is clamped to 0 — without
            that the column expands to its grid items' intrinsic
            min-content width, which on case pages includes the Header's
            wide case sub-nav box and pushes the page off-screen. */}
        <div
          className={classNames('bg-fletchers-gray-25 grid min-h-0 w-full flex-1 gap-0', {
            'grid-cols-[320px_minmax(0,1fr)]': hasLeftSidebar,
            'grid-cols-[minmax(0,1fr)]': !hasLeftSidebar,
            'grid-rows-[48px_48px_48px_1fr]': withNavigation,
            'grid-rows-[48px_48px_1fr]': !withNavigation,
          })}
        >
          {/* `relative z-[1102]` lifts the header above any case-page
              sidebar so the nav stays in front while one is open.
              CreateEditCaseSidebar pins its mask to `!z-[1000]` and
              CloneCaseSidebar uses `z-[1100]/1101` via `pt`; 1102 beats
              both. `relative` is required for `z-index` to take effect on
              a non-positioned element. */}
          <div className="relative z-[1102] col-span-full">
            <Header />
          </div>

          {/* 320px left rail. Spans every row below the header
              (3 with TopPageNavigation, 2 without). `border-r` keeps a
              visible seam against the main content; bg-white sits over
              the page's gray-25 base. */}
          {hasLeftSidebar && (
            <aside
              className={classNames(
                'border-fletchers-gray-110 col-start-1 row-start-2 overflow-y-auto border-r bg-white',
                withNavigation ? 'row-span-3' : 'row-span-2',
              )}
            >
              {leftSidebar}
            </aside>
          )}

          {withNavigation && (
            <div className={classNames('content-center', hasLeftSidebar ? 'col-start-2' : 'col-span-full')}>
              <TopPageNavigation buttonLabel="Back to Cases" handleBack={onBackTopPageNavigationClick} />
            </div>
          )}

          <div className={hasLeftSidebar ? 'col-start-2' : 'col-span-full'}>{actionsPanel}</div>

          <div
            className={classNames('h-full w-full overflow-hidden', hasLeftSidebar ? 'col-start-2' : 'col-span-full')}
          >
            {isDataLoading ? <LoadingSpinner /> : children}
          </div>
        </div>
      </div>
    </Layout>
  );
};
